<template>
	<div id="om-app">
		<core-alert-bar />
		<transition name="fade" mode="out-in" @after-enter="triggerScroll" @after-leave="triggerScroll">
			<router-view :key="$route.fullPath" @hook:mounted="routerComponentMounted" />
		</transition>
		<common-quick-links />
		<modals-pro-feature v-bind="upsellProps" />
		<common-upgrade-comms />
	</div>
</template>
<script lang="js">
import isObject from 'lodash/isObject';
import { mapActions, mapGetters, mapState } from 'vuex';
import { getAppData } from '@/utils';

export default {
	data() {
		return {
			upsellProps: {
				utmMedium: 'CampaignsTable',
				features: [],
			},
			timeout: null,
		};
	},
	computed: {
		...mapState('dashboard', ['onboardingPlugins']),
		...mapGetters('plugins', { getPluginBySlug: 'bySlug' }),
		isLoading() {
			return this.$store.getters.isLoading('plugins');
		},
	},
	mounted() {
		this.$bus.$on('launchSetupWizard', this.launchSetupWizard);
		this.$bus.$on('toggleProModal', this.toggleProModal);
		this.$bus.$on('fetchedWpResources', this.maybeUpdateMe);

		if (this.$store.getters.shouldFetchUser) {
			this.fetchMe();
		}

		// Look for the onboarding query param and launch the setup wizard if found.
		const urlParams = new URLSearchParams(window.location.search);
		const isOnboarding = urlParams.get('onboarding');

		if (isOnboarding) {
			this.launchSetupWizard();
		}

		if (Array.isArray(this.onboardingPlugins) && this.onboardingPlugins.length) {
			// Always re-fetch before installing. The install POST is rejected without a valid
			// per-plugin `actionNonce`, and the GET response is the only thing that mints one, so
			// installing from an already-loaded list can send an expired token. Previously this
			// branch fetched without chaining the install, and the `plugins` watcher that was meant
			// to catch the fetch only fired when the *previous* list was non-empty — which it never
			// is on a first load — so the add-ons a user picked during onboarding silently never
			// installed. See #944.
			this.fetchPlugins()
				.then(this.maybeInstallPlugins)
				// `fetch` already reports failures through the store, so swallow the rejection
				// here rather than raising an unhandled one. Without a fetch there is no fresh
				// nonce, so there is nothing to install.
				.catch(() => {});
		}
	},
	beforeDestroy() {
		this.$bus.$off('toggleProModal', this.toggleProModal);
		this.$bus.$off('fetchedWpResources', this.maybeUpdateMe);
		this.$bus.$off('launchSetupWizard', this.launchSetupWizard);
	},
	methods: {
		...mapActions(['fetchMe', 'updateMe']),
		...mapActions('plugins', {
			fetchPlugins: 'fetch',
			pluginAction: 'action',
		}),
		...mapActions('settings', ['saveMiscSettings']),
		triggerScroll() {
			this.$root.$emit('triggerScroll');
		},
		toggleProModal(utmMedium = 'CampaignsTable', features = []) {
			this.upsellProps = isObject(utmMedium) ? utmMedium : { utmMedium, features };

			this.$modal.show('pro-feature');
		},
		maybeUpdateMe() {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}
			this.timeout = setTimeout(() => this.updateMe(), 1000);
		},
		maybeInstallPlugins() {
			const promises = [];
			this.onboardingPlugins.forEach((slug) => {
				const plugin = this.getPluginBySlug(slug);
				if ('install' === plugin?.action) {
					const promise = this.pluginAction(plugin);
					promises.push(promise);
				}
			});

			Promise.allSettled(promises).then(() => {
				this.saveMiscSettings({ resetOnboardingPlugins: true });
			});
		},
		routerComponentMounted() {
			this.$bus.$emit('routerComponentMounted');
		},
		launchSetupWizard() {
			try {
				const redirect = new URL(this.$urls.app('wp-welcome'));
				redirect.searchParams.set('connectionToken', getAppData('connectionToken', ''));
				redirect.searchParams.set('wpUrl', this.$urls.wp());
				redirect.searchParams.set('restUrl', this.$urls.wpRest());
				redirect.searchParams.set('adminUrl', this.$urls.wpAdmin());
				redirect.searchParams.set('v', getAppData('pluginVersion', ''));
				redirect.searchParams.set('siteName', getAppData('blogname', ''));
				redirect.searchParams.set('email', getAppData('userEmail', ''));
				redirect.searchParams.set('firstName', getAppData('userFirstName', ''));
				redirect.searchParams.set('lastName', getAppData('userLastName', ''));
				// redirect.searchParams.set('nonce', getAppData('nonce', ''));

				if (getAppData('partnerId')) {
					redirect.searchParams.set('partnerId', getAppData('partnerId', ''));
				}

				if (getAppData('partnerUrl')) {
					redirect.searchParams.set('partnerUrl', getAppData('partnerUrl', ''));
				}

				if (getAppData('referredBy')) {
					redirect.searchParams.set('referredBy', getAppData('referredBy', ''));
				}

				if (getAppData('isWooActive', '') || getAppData('isWooConnected', '')) {
					const woo = [];

					if (getAppData('isWooActive', '')) {
						woo.push('active');
					}

					if (getAppData('isWooConnected', '')) {
						woo.push('connected');
					}

					redirect.searchParams.set('wc', woo);
				}

				window.location.href = redirect;
			} catch (e) {}
		},
	},
};
</script>
<style lang="scss">
@import 'assets/scss/app';
.omapi-screen {
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.2s;
	}
	.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
		opacity: 0;
	}
}
</style>
